import { useEffect, useMemo, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ref, onValue } from 'firebase/database'
import { db, tref } from '../../lib/firebase.js'

import GShell from './GShell.jsx'
import CLUB from '../../config/club.js'
import { Card, Pill, money, fmtDate } from './gui.jsx'
import { watchMember, updateMember, setMemberActive, deleteMember } from '../../lib/members.js'

const PRIMARY = CLUB.primaryColor || 'var(--ge-primary)'
const ACCENT = CLUB.accentColor || 'var(--ge-primary)'

// Watch a /club node once and expose its value map.
function useNode(path) {
  const [v, setV] = useState({})
  useEffect(() => { const off = onValue(tref(path), (s) => setV(s.val() || {})); return () => off() }, [path])
  return v
}

export default function GMemberProfile() {
  const { memberId } = useParams()
  const navigate = useNavigate()
  const [member, setMember] = useState(undefined)
  useEffect(() => { const off = watchMember(memberId, setMember); return () => off() }, [memberId])

  const repairs = useNode('club/repairs')
  const demos = useNode('club/demoClubs')
  const rentals = useNode('club/rentals')
  const sales = useNode('club/sales')
  const lessons = useNode('club/lessons')
  const orders = useNode('club/specialOrders')
  const credit = useNode('club/proShopCredit')

  const byMember = (obj) => Object.values(obj || {}).filter((r) => r && r.memberId === memberId)
  const mRepairs = byMember(repairs), mDemos = byMember(demos), mRentals = byMember(rentals), mSales = byMember(sales), mLessons = byMember(lessons)
  // Credit & Special Orders predate memberId — best-effort match by name.
  const nm = (member?.name || '').trim().toLowerCase()
  const mOrders = Object.values(orders || {}).filter((o) => o && (o.memberId === memberId || (nm && String(o.name || '').trim().toLowerCase() === nm)))
  const creditAccts = Object.entries(credit.accounts || {}).filter(([, a]) => nm && String(a.name || '').trim().toLowerCase() === nm)
    .map(([id, a]) => ({ id, name: a.name, balance: Object.values(a.transactions || {}).reduce((s, t) => s + (t.type === 'redeem' ? -1 : 1) * (Number(t.amount) || 0), 0) }))
  const purchaseTotal = mSales.reduce((s, x) => s + (Number(x.total) || 0), 0)

  return (
    <GShell>
      {member === undefined ? <p className="text-sm text-gray-500">Loading…</p> : member === null ? <p className="text-sm text-gray-500">Member not found.</p> : (
        <>
          <IdentityCard member={member} onDeleted={() => navigate('/admin/gg/members')} />

          <div className="text-xs font-extrabold uppercase tracking-widest" style={{ color: PRIMARY }}>Member 360 · all activity</div>
          <div className="grid md:grid-cols-2 gap-3">
            <Section title="💳 Shop Credit" count={creditAccts.length}>
              {creditAccts.length ? creditAccts.map((a) => <Row key={a.id} left={a.name} right={money(a.balance)} />) : <Empty note="No credit account (matched by name)." />}
            </Section>
            <Section title="📦 Special Orders" count={mOrders.length}>
              {mOrders.length ? mOrders.map((o) => <Row key={o.id} left={o.item} right={<Pill>{o.status}</Pill>} />) : <Empty />}
            </Section>
            <Section title="🔧 Repairs" count={mRepairs.length}>
              {mRepairs.length ? mRepairs.map((r) => <Row key={r.id} left={r.club} right={<Pill>{r.status}</Pill>} />) : <Empty />}
            </Section>
            <Section title="🏌️ Demo Clubs" count={mDemos.length}>
              {mDemos.length ? mDemos.map((d) => <Row key={d.id} left={d.club} right={<Pill>{d.returned ? 'Returned' : 'Out'}</Pill>} />) : <Empty />}
            </Section>
            <Section title="🛍️ Rentals" count={mRentals.length}>
              {mRentals.length ? mRentals.map((r) => <Row key={r.id} left={r.itemType} right={<Pill>{r.status}</Pill>} />) : <Empty />}
            </Section>
            <Section title={`🛒 Purchases · ${money(purchaseTotal)}`} count={mSales.length}>
              {mSales.length ? mSales.slice(0, 8).map((s) => <Row key={s.id} left={fmtDate(new Date(s.timestamp).toISOString().slice(0, 10))} right={money(s.total)} />) : <Empty />}
            </Section>
            <Section title="⛳ Lessons" count={mLessons.length}>
              {mLessons.length ? mLessons.map((l) => <Row key={l.id} left={l.packageName || 'Lesson'} right={<Pill>{l.status}</Pill>} />) : <Empty />}
            </Section>
            <Section title="🏆 Handicap & Recent Rounds" count={null}>
              <Empty note="Tournament-data hookup pending — wire to the existing scoring data here (read-only)." />
            </Section>
          </div>
        </>
      )}
    </GShell>
  )
}

function Section({ title, count, children }) {
  return (
    <Card>
      <div className="flex items-center justify-between mb-1">
        <div className="font-extrabold text-sm" style={{ color: PRIMARY }}>{title}</div>
        {count != null ? <Pill bg={`${ACCENT}22`} fg="var(--ge-primary)">{count}</Pill> : null}
      </div>
      <div className="text-sm space-y-0.5">{children}</div>
    </Card>
  )
}
function Row({ left, right }) {
  return <div className="flex items-center justify-between gap-2 border-b py-0.5" style={{ borderColor: 'rgba(0,0,0,0.05)' }}><span className="truncate">{left}</span><span className="shrink-0 text-gray-600">{right}</span></div>
}
function Empty({ note }) { return <p className="text-xs text-gray-400">{note || 'None yet.'}</p> }

function IdentityCard({ member, onDeleted }) {
  const [editing, setEditing] = useState(false)
  const [f, setF] = useState(member)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  useEffect(() => { setF(member) }, [member])
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }))
  const save = async () => { setBusy(true); setErr(''); try { await updateMember(member.id, f); setEditing(false) } catch (e) { setErr(e?.message || 'Failed') } finally { setBusy(false) } }
  const del = async () => { if (!window.confirm(`Delete member ${member.name}?`)) return; try { await deleteMember(member.id); onDeleted() } catch (e) { setErr(e?.message || 'Failed') } }
  return (
    <Card>
      {!editing ? (
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-2xl font-extrabold" style={{ color: PRIMARY, fontFamily: 'Cinzel, serif' }}>{member.name}{member.active === false ? <span className="ml-2 text-[10px] uppercase tracking-widest text-gray-400 align-middle">inactive</span> : null}</div>
            <div className="text-sm text-gray-600 mt-1 space-y-0.5">
              {member.memberNumber ? <div>Member #{member.memberNumber}</div> : null}
              {member.phone ? <div>📞 {member.phone}</div> : null}{member.email ? <div>✉️ {member.email}</div> : null}
              {member.favoriteBrands ? <div>⭐ {member.favoriteBrands}</div> : null}{member.notes ? <div className="text-gray-500">📝 {member.notes}</div> : null}
            </div>
          </div>
          <div className="flex flex-col items-end gap-1 shrink-0">
            <button className="btn-secondary text-xs" onClick={() => setEditing(true)}>Edit</button>
            <button className="btn-ghost text-xs" style={{ color: ACCENT }} onClick={() => setMemberActive(member.id, member.active === false)}>{member.active === false ? 'Reactivate' : 'Deactivate'}</button>
            <button className="btn-ghost text-xs" style={{ color: '#B91C1C' }} onClick={del}>Delete</button>
          </div>
        </div>
      ) : (
        <div>
          <div className="grid sm:grid-cols-2 gap-2">
            {[['name', 'Name'], ['memberNumber', 'Member #'], ['phone', 'Phone'], ['email', 'Email'], ['favoriteBrands', 'Favorite brands'], ['notes', 'Notes']].map(([k, ph]) => (
              <input key={k} className="input" placeholder={ph} value={f[k] || ''} onChange={set(k)} />
            ))}
          </div>
          <div className="flex justify-end gap-2 mt-3"><button className="btn-ghost text-sm" onClick={() => { setEditing(false); setF(member) }}>Cancel</button><button className="btn-primary text-sm" onClick={save} disabled={busy || !String(f.name || '').trim()}>{busy ? 'Saving…' : 'Save'}</button></div>
        </div>
      )}
      {err ? <p className="text-xs font-bold mt-2" style={{ color: '#B91C1C' }}>{err}</p> : null}
    </Card>
  )
}
